import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../components/Modal.tsx';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { colourHex } from '../lib/format.ts';
import { ChevronLeft, ChevronRight, Drag, Kebab, LayoutGrid, Plus } from '../lib/icons.tsx';
import { startPointerDrag } from '../lib/pointerDrag.ts';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';
import { usePersistedState } from '../lib/persistedState.ts';
import { useIframeDragGuard } from '../lib/useIframeDragGuard.ts';
import { groupBlocks, resolveReorder, resolveMultiReorder } from '../lib/slideBlocks.ts';
import SectionHeader from './SectionHeader.tsx';
import SlideContextMenu from './SlideContextMenu.tsx';
import AddSectionForm from './AddSectionForm.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import { cssVars } from '../lib/css.ts';
import type { DragEndEvent } from '@dnd-kit/core';
import type { Dispatch, PointerEvent as ReactPointerEvent, ReactNode, RefObject, SetStateAction } from 'react';
import type { DeckSection, SectionRef, SlideEntry } from '../../shared/deck.ts';
import type { SlideBlock, SectionMove } from '../lib/slideBlocks.ts';
import type { NewSection } from '../editor/useDeckEditor.ts';

/**
 * The per-slide activity dot. Wider than chatTurns' `ActivityState`: Canvas
 * adds `done` for a slide a running selection turn has already written, so
 * the wait reads as progress rather than a stall.
 */
export type SlideActivity = 'streaming' | 'unread' | 'done';

/** What the confirm dialog is about. Two shapes, and the section arm carries
 *  the count because the copy offers to keep that many slides. */
type FilmstripConfirm =
  | { kind: 'slide'; slide: SlideEntry; multiCount: number }
  | { kind: 'section'; section: SectionRef; count: number }
  | null;

type FilmstripProps = {
  slug?: string | null;
  sections?: DeckSection[];
  slides?: SlideEntry[];
  activeIdx?: number;
  activity?: Record<string, SlideActivity | null> | null;
  /** Proposed NEW slides sitting in the deck, awaiting keep/discard. */
  pending?: Set<string> | null;
  /** Existing slides with an AI EDIT awaiting keep/discard. */
  review?: Set<string> | null;
  /** Slides carrying a BLOCKING validation finding — a logo wall on the demo
   *  brands, a team wearing colleagues' faces. Publish is held until they clear,
   *  so the tag exists to say WHERE while you are still editing, rather than
   *  only when the Publish drawer is opened. */
  blocked?: Set<string> | null;
  /** Pick a slide. OPTIONAL: a surface with nothing to show for a selection
   *  (the build watcher before it held one) omits it, and the tile then renders
   *  disabled rather than swallowing the click — an enabled-looking control that
   *  does nothing is worse than a disabled one (Alex, 2026-08-18). */
  onSelect?: ((idx: number) => void) | null;
  /** Open the whole-deck overview grid. */
  onShowOverview?: (() => void) | null;
  /** Opens the add-slide chooser. `null` section = the deck's tail. */
  onAddSlide?: ((sectionKey: string | null, afterSlide?: SlideEntry) => void) | null;
  /** Takes the moved BLOCK's first slide, not the block — `commitReorder`
   *  wants one slide to follow after the write. */
  onReorderCommit?: ((newList: SlideEntry[], movedSlide: SlideEntry, toSection: string | null) => void) | null;
  onReorderMultiCommit?:
    | ((
        newList: SlideEntry[],
        movedBlocks: SlideBlock[],
        toSection: string | null,
        sectionMoves: SectionMove[],
      ) => void)
    | null;
  onDuplicate?: ((slide: SlideEntry) => void) | null;
  /** Rewrite the slide's meta title. */
  onRenameSlide?: ((slide: SlideEntry, title: string) => void) | null;
  onMoveToSection?: ((slide: SlideEntry, sectionKey: string) => void) | null;
  onChangeTemplate?: ((slide: SlideEntry) => void) | null;
  onToggleHidden?: ((slide: SlideEntry) => void) | null;
  onSaveToBank?: ((slide: SlideEntry, bank: string) => void) | null;
  onDeleteSlide?: ((slide: SlideEntry) => void) | null;
  onRenameSection?: ((section: SectionRef, name: string) => void) | null;
  onRecolourSection?: ((section: SectionRef, color: string) => void) | null;
  /** THREE arguments, not two: the third is the section to move the slides
   *  into instead of deleting them ("Keep the slides"). */
  onDeleteSection?: ((section: SectionRef, count: number, moveSlidesTo?: string | null) => void) | null;
  /** Used by the inline form (library overlay) AND the modal's empty path. */
  onCreateSection?: ((section: NewSection) => void) | null;
  /** Editor path: when set, "+ Section" opens the modal instead of the form. */
  onAddSection?: (() => void) | null;
  onMoveSection?: ((section: SectionRef, delta: -1 | 1) => void) | null;
  /** Drag a COLLAPSED section row. `label` is the undo toast's wording. */
  onReorderSections?: ((newContentKeys: string[], label: string) => void) | null;
  /** Controlled selection of block baseIds — Canvas lifts it into chat scope.
   *  Internal state stands in when absent (library overlay, intake). */
  selected?: Set<string>;
  onSelectedChange?: Dispatch<SetStateAction<Set<string>>>;
  busy?: boolean;
  generating?: boolean;
};

/**
 * Left-edge filmstrip: the deck's structural surface. Slides group under
 * sticky section headers (rename, recolour, add, delete — all direct);
 * tiles drag to reorder (cross-section drops move the slide), and each tile
 * has a kebab/right-click menu for duplicate / move / delete. Generative
 * work (writing a new slide) still goes through chat — the "+" buttons seed
 * the command bar rather than creating blank slides.
 *
 * Multi-instance slides (multi: N) share one manifest row, so their tiles
 * render as one draggable block and delete/move act on every instance —
 * hence one <li> per BLOCK, holding 1..N tiles.
 *
 * Reordering runs on dnd-kit. It replaced a hand-rolled pointer-capture
 * gesture whose only exit was a pointerup landing back on the grip: lose the
 * capture (focus stolen, context menu, the pointer crossing the preview
 * iframe) and the drag state stuck, greying the row out until a page reload.
 * dnd-kit owns the lifecycle — window-level listeners, cancel on Escape/blur —
 * so the gesture always terminates.
 *
 * A COLLAPSED section is one object, so its row is sortable too (id
 * `sec:<key>`) and dragging it reorders whole sections — the ⋮ menu's Move
 * up/down without the one-step-at-a-time. It commits through the section-order
 * endpoint, never the slide reorder path.
 *
 * We deliberately do NOT apply useSortable's transform: section headers are
 * interleaved between the sortable items, which the list strategies don't
 * measure, so shifting tiles would drift over the headings. The drop target
 * shows as an explicit insertion bar (driven by dnd-kit's `over`) plus a
 * DragOverlay, and static rects keep collision detection honest.
 */
/** A collapsed section's sortable id. Namespaced so it can share the slide
 *  blocks' DndContext without ever colliding with a slide's baseId. */
const sectionDragId = (key: string) => 'sec:' + key;
const isSectionId = (id: string | null | undefined): boolean => typeof id === 'string' && id.startsWith('sec:');
const sectionIdKey = (id: string) => id.slice(4);

export default function Filmstrip({
  slug,
  sections,
  slides,
  activeIdx,
  activity,
  pending,
  review,
  blocked,
  onSelect,
  onShowOverview,
  onAddSlide,
  onReorderCommit,
  onReorderMultiCommit,
  onDuplicate,
  onRenameSlide,
  onMoveToSection,
  onChangeTemplate,
  onToggleHidden,
  onSaveToBank,
  onDeleteSlide,
  onRenameSection,
  onRecolourSection,
  onDeleteSection,
  onCreateSection,
  onAddSection,
  onMoveSection,
  onReorderSections,
  selected: controlledSelected,
  onSelectedChange,
  busy,
  generating,
}: FilmstripProps) {
  const sectionByKey = useMemo(() => new Map((sections || []).map((s) => [s.key, s])), [sections]);
  // Content sections in document order — intro/closing never move, so these
  // are the only keys "Move section up/down" operates over.
  const contentKeys = useMemo(
    () => (sections || []).map((s) => s.key).filter((k) => k !== 'intro' && k !== 'closing'),
    [sections],
  );
  // New-deck intake renders the filmstrip without the structural callbacks —
  // there it's a read-only progress list (no grips, kebabs or section edits).
  const editable = Boolean(onReorderCommit && onDuplicate && onDeleteSlide);
  // Phone: the strip lies along the top of the stacked layout — always
  // expanded (a vertical rail makes no sense there), CSS-sized, no resize.
  const isPhone = useMediaQuery(PHONE_QUERY);
  const activeRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  // User-dragged width (px). null = CSS default.
  const [width, setWidth] = useState<number | null>(() => {
    const v = Number(localStorage.getItem('studio.filmstripWidth'));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  // No stored preference → default collapsed on narrow (tablet) windows,
  // where the preview needs the width more than the slide list does.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('studio.filmstripCollapsed');
    if (stored != null) return stored === '1';
    return window.innerWidth < 1100;
  });
  // Collapsed section keys, per deck. Opt-in — long decks only.
  const [collapsedSections, setCollapsedSections] = usePersistedState<Record<string, true>>(
    'studio.collapsedSections.' + (slug || 'new'),
    {},
  );
  const [menuFor, setMenuFor] = useState<string | null>(null); // slide.id with an open kebab menu
  const [confirm, setConfirm] = useState<FilmstripConfirm>(null);
  const [renaming, setRenaming] = useState<SlideEntry | null>(null); // slide being renamed via the ⋮ menu
  const [addingSection, setAddingSection] = useState(false);
  // dnd-kit drag state — the base ids of the block being dragged and the one
  // it is currently over. Both are cleared by dnd-kit's own end/cancel path.
  // Held as strings: every id here is one WE supplied (a block's baseId or a
  // `sec:` key), so dnd-kit's number-or-string `UniqueIdentifier` is wider
  // than anything that can actually arrive.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  // Multi-select — blocks ticked via the hover checkbox (shift = range from
  // the anchor, cmd/ctrl+click = toggle accelerator). Selection is keyed on
  // baseId so it survives refreshes; a drag on a selected tile moves the lot.
  // Controlled when the parent passes `selected` + `onSelectedChange` (the
  // editor lifts it into chat scope); internal otherwise (library overlay,
  // intake), so those callers need no new plumbing.
  const [internalSelected, setInternalSelected] = useState<Set<string>>(() => new Set());
  const selected = controlledSelected ?? internalSelected;
  const setSelected = onSelectedChange ?? setInternalSelected;
  const [anchorId, setAnchorId] = useState<string | null>(null);
  // True mid-resize so the collapse width-transition is suppressed (a live
  // drag would otherwise ease every pointermove and lag).
  const [resizing, setResizing] = useState(false);

  // Consecutive slides sharing a file (multi: N) form one manifest row —
  // one draggable block. Everything structural operates on blocks.
  const blocks = useMemo(() => groupBlocks(slides), [slides]);

  // ----- multi-select ------------------------------------------------------

  // Drop selected ids whose blocks no longer exist (deletes, refreshes).
  useEffect(() => {
    setSelected((cur) => {
      if (!cur.size) return cur;
      const present = new Set(blocks.map((b) => b.baseId));
      const next = new Set([...cur].filter((id) => present.has(id)));
      return next.size === cur.size ? cur : next;
    });
    // setSelected is a state setter (or the parent's callback standing in for
    // one) — it only ever sets, so a changed identity can't make this stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  function clearSelection() {
    setSelected(new Set());
    setAnchorId(null);
  }

  function toggleSelect(baseId: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(baseId)) next.delete(baseId);
      else next.add(baseId);
      return next;
    });
    setAnchorId(baseId);
  }

  /** Shift-click: select the run between the anchor and the target. */
  function rangeSelect(baseId: string) {
    const anchorIdx = anchorId ? blocks.findIndex((b) => b.baseId === anchorId) : -1;
    if (anchorIdx < 0) return toggleSelect(baseId);
    const targetIdx = blocks.findIndex((b) => b.baseId === baseId);
    if (targetIdx < 0) return;
    const [lo, hi] = anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
    setSelected((cur) => {
      const next = new Set(cur);
      // Both ends came from findIndex over `blocks`, so every index in the run
      // is real; `?.` says that without an assertion.
      for (let i = lo; i <= hi; i++) {
        const id = blocks[i]?.baseId;
        if (id) next.add(id);
      }
      return next;
    });
  }

  // Escape clears the selection — unless a drag owns Escape right now, a menu
  // or confirm is open (they close on Escape themselves), or focus is in a field.
  useEffect(() => {
    if (!selected.size) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || activeId || menuFor || confirm) return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      )
        return;
      clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // clearSelection is redeclared every render but only calls setters — listing
    // it would rebind the keydown listener on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.size, activeId, menuFor, confirm]);

  const selectedCount = useMemo(
    () => blocks.reduce((n, b) => n + (selected.has(b.baseId) ? b.items.length : 0), 0),
    [blocks, selected],
  );

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      // Explicit '0' (not removal) so an expand survives the tablet default.
      localStorage.setItem('studio.filmstripCollapsed', next ? '1' : '0');
      return next;
    });
  }

  function toggleSection(key: string) {
    setCollapsedSections((cur) => {
      const next = { ...cur };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }

  // Keep the active tile in view as the user navigates — the strip scrolls
  // vertically on desktop, horizontally on phones; nearest covers both.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeIdx]);

  // Pointer capture keeps the drag alive when the cursor crosses the
  // preview iframe (which would otherwise swallow pointer events).
  function onResizeStart(e: ReactPointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = e.currentTarget.parentElement?.offsetWidth || width || 168;
    const clamp = (w: number) => Math.min(Math.max(w, 96), Math.round(window.innerWidth * 0.4));
    let w = startW;
    setResizing(true);
    startPointerDrag(e, {
      onMove(ev) {
        w = clamp(startW + (ev.clientX - startX));
        setWidth(w);
      },
      onEnd() {
        setResizing(false);
        localStorage.setItem('studio.filmstripWidth', String(w));
      },
    });
  }

  function onResizeReset() {
    setWidth(null);
    localStorage.removeItem('studio.filmstripWidth');
  }

  // ----- drag-to-reorder (dnd-kit) -----------------------------------------

  // Mouse only, with a 5px threshold: the whole tile drags, but a plain click
  // still selects. No TouchSensor — a whole-tile touch drag would fight the
  // list's own scrolling, so touch keeps the ⋮ menu's move verbs (the grip is
  // display:none under `@media (hover: none)`).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // The preview iframe would otherwise eat a mouseup released over it.
  useIframeDragGuard(activeId);

  const dragFromIdx = activeId ? blocks.findIndex((b) => b.baseId === activeId) : -1;
  const dragToIdx = overId ? blocks.findIndex((b) => b.baseId === overId) : -1;

  // Finder rule: dragging a SELECTED tile drags the whole selection; dragging
  // an unselected tile drags just it and leaves the selection alone.
  const dragIdSet = useMemo<Set<string>>(() => {
    if (!activeId) return new Set();
    if (!selected.has(activeId) || !onReorderMultiCommit) return new Set([activeId]);
    return new Set(blocks.filter((b) => selected.has(b.baseId)).map((b) => b.baseId));
  }, [activeId, selected, blocks, onReorderMultiCommit]);

  function handleDragEnd({ active, over }: DragEndEvent) {
    const ids = dragIdSet;
    const activeDragId = String(active.id);
    setActiveId(null);
    setOverId(null);
    if (!over) return;
    const overDragId = String(over.id);
    // A COLLAPSED SECTION was dragged: this is a section move, never a slide
    // move. It must not go through resolveMultiReorder — that retags every
    // moved slide into whichever section it lands beside, which is the exact
    // opposite of what moving a section means.
    if (isSectionId(activeDragId)) {
      const from = sectionIdKey(activeDragId);
      // Dropping onto a slide inside another section means "put me where that
      // SECTION is" — the drop always resolves to a section, never a slide slot.
      const to = isSectionId(overDragId)
        ? sectionIdKey(overDragId)
        : blocks.find((b) => b.baseId === overDragId)?.section;
      if (!to || to === from) return;
      const order = contentKeys.slice();
      const i = order.indexOf(from);
      const j = order.indexOf(to);
      if (i < 0 || j < 0) return; // intro/closing are pinned — never reorderable
      order.splice(i, 1);
      order.splice(order.indexOf(to) + (i < j ? 1 : 0), 0, from);
      const name = sectionByKey.get(from)?.name || from;
      onReorderSections?.(order, `Moved "${name}"`);
      return;
    }
    const fromIdx = blocks.findIndex((b) => b.baseId === activeDragId);
    const toIdx = blocks.findIndex((b) => b.baseId === overDragId);
    if (ids.size > 1) {
      const move = resolveMultiReorder(blocks, ids, fromIdx, toIdx);
      if (move) onReorderMultiCommit?.(move.newList, move.moved, move.toSection, move.sectionMoves);
      return;
    }
    const move = resolveReorder(blocks, fromIdx, toIdx);
    // A block always holds at least one slide, so the guard is what makes
    // that readable rather than a case that happens.
    const first = move?.moved.items[0];
    if (move && first) onReorderCommit?.(move.newList, first, move.toSection);
  }

  // ----- render ------------------------------------------------------------

  if (collapsed && !isPhone) {
    return (
      <aside className="filmstrip filmstrip--collapsed" aria-label="Slides">
        {/* Overview sits at the TOP of the rail, under the expand chevron and in
            the same order as the expanded head's actions. It used to be the last
            child, which the toggle's flex:1 pushed to the floor of a 1000px rail
            — present, but nowhere near where you look for it, so the overview
            read as something you had to open the filmstrip to reach
            (Jake, 2026-08-01). The toggle still grows to fill the rest, so any
            click below it opens the filmstrip. */}
        <div className="rail-head">
          <button
            type="button"
            className="iconbtn rail-head__btn"
            onClick={toggleCollapsed}
            title="Show the filmstrip"
            aria-label="Show slides"
          >
            <ChevronRight />
          </button>
          {onShowOverview && (slides || []).length > 0 && (
            <button
              type="button"
              className="iconbtn rail-head__btn"
              onClick={onShowOverview}
              title="Overview: see all slides"
              aria-label="Overview: see all slides"
            >
              <LayoutGrid />
            </button>
          )}
        </div>
        <button
          type="button"
          className="rail-toggle rail-toggle--headless"
          onClick={toggleCollapsed}
          title="Show the filmstrip"
          aria-label="Show slides"
        >
          <span className="rail-toggle__label">Slides</span>
          {(slides || []).length > 0 && <span className="rail-toggle__count">{slides?.length}</span>}
        </button>
      </aside>
    );
  }

  const rows: ReactNode[] = [];
  const sectionRow = (sectionKey: string, rowKey: string) => {
    // A key the manifest doesn't list still gets a row — SectionHeader's own
    // `HeaderSection` is exactly this partial shape for that reason.
    const section = sectionByKey.get(sectionKey) || { key: sectionKey, name: sectionKey, n: null, color: '' };
    const count = (slides || []).filter((s) => s.section === sectionKey).length;
    const contentIdx = contentKeys.indexOf(sectionKey);
    const draggable =
      editable &&
      Boolean(onReorderSections) &&
      collapsedSections[sectionKey] &&
      contentIdx >= 0 &&
      contentKeys.length > 1;
    if (draggable) {
      return (
        <SortableSectionRow key={rowKey} sectionKey={sectionKey}>
          {(grip) => (
            <SectionHeader
              section={section}
              count={count}
              collapsed
              dragHandle={grip}
              onToggleCollapse={() => toggleSection(sectionKey)}
              onRename={(name) => onRenameSection?.(section, name)}
              onRecolour={(color) => onRecolourSection?.(section, color)}
              onAddSlide={() => onAddSlide?.(sectionKey)}
              onMoveUp={onMoveSection && contentIdx > 0 ? () => onMoveSection(section, -1) : null}
              onMoveDown={onMoveSection && contentIdx < contentKeys.length - 1 ? () => onMoveSection(section, 1) : null}
              movable={Boolean(onMoveSection)}
              onDelete={() => setConfirm({ kind: 'section', section, count })}
              busy={busy}
            />
          )}
        </SortableSectionRow>
      );
    }
    return (
      <li key={rowKey} className="filmstrip__section-row">
        {editable ? (
          <SectionHeader
            section={section}
            count={count}
            collapsed={!!collapsedSections[sectionKey]}
            onToggleCollapse={() => toggleSection(sectionKey)}
            onRename={(name) => onRenameSection?.(section, name)}
            onRecolour={(color) => onRecolourSection?.(section, color)}
            onAddSlide={() => onAddSlide?.(sectionKey)}
            onMoveUp={onMoveSection && contentIdx > 0 ? () => onMoveSection(section, -1) : null}
            onMoveDown={
              onMoveSection && contentIdx >= 0 && contentIdx < contentKeys.length - 1
                ? () => onMoveSection(section, 1)
                : null
            }
            movable={Boolean(onMoveSection) && contentIdx >= 0 && contentKeys.length > 1}
            onDelete={
              section.key !== 'intro' && section.key !== 'closing'
                ? () => setConfirm({ kind: 'section', section, count })
                : null
            }
            busy={busy}
          />
        ) : (
          <div className="strip-section strip-section--readonly">
            <span
              className="strip-section__dot strip-section__dot--static"
              style={cssVars({ '--swatch': colourHex(section.color || 'ORANGE') })}
              aria-hidden="true"
            />
            <span className="strip-section__name">
              {section.n ? section.n + ' · ' : ''}
              {section.name || section.key}
            </span>
          </div>
        )}
      </li>
    );
  };
  const sortableIds: string[] = [];
  let prevSection: string | null = null;
  blocks.forEach((block, blockIdx) => {
    if (block.section !== prevSection) {
      prevSection = block.section;
      rows.push(sectionRow(block.section, 'section:' + block.section + ':' + blockIdx));
      // A collapsed section is ONE sortable object standing in for its blocks.
      if (collapsedSections[block.section]) sortableIds.push(sectionDragId(block.section));
    }
    if (collapsedSections[block.section]) return;
    sortableIds.push(block.baseId);

    // The insertion bar sits before the target block when dragging upwards and
    // after it when dragging down — the gap the block would land in. Dropping
    // onto a dragged member is a no-op, so no bar shows there.
    const isTarget =
      dragToIdx === blockIdx && dragFromIdx >= 0 && dragFromIdx !== blockIdx && !dragIdSet.has(block.baseId);
    rows.push(
      <SortableSlideBlock
        key={block.baseId}
        block={block}
        isSelected={selected.has(block.baseId)}
        isDragMember={dragIdSet.has(block.baseId)}
        onToggleSelect={onReorderMultiCommit ? toggleSelect : null}
        onRangeSelect={rangeSelect}
        hasSelection={selected.size > 0}
        sectionByKey={sectionByKey}
        sections={sections}
        activeIdx={activeIdx}
        activeRef={activeRef}
        activity={activity}
        pending={pending}
        review={review}
        blocked={blocked}
        editable={editable}
        busy={busy}
        generating={generating}
        dropBefore={isTarget && dragFromIdx > blockIdx}
        dropAfter={isTarget && dragFromIdx < blockIdx}
        menuFor={menuFor}
        setMenuFor={setMenuFor}
        onSelect={onSelect}
        onAddSlide={onAddSlide}
        onDuplicate={onDuplicate}
        onRequestRename={onRenameSlide ? setRenaming : null}
        onMoveToSection={onMoveToSection}
        onChangeTemplate={onChangeTemplate}
        onToggleHidden={onToggleHidden}
        onSaveToBank={onSaveToBank}
        onRequestDelete={setConfirm}
      />,
    );
  });
  // Sections with no slides yet — possible in the bank deck (library items
  // carry no divider stubs), where a just-created internal section must stay
  // visible so its + / rename / delete remain reachable. Rendered at the
  // tail; once a slide lands the section takes its real position above.
  if (editable) {
    const populated = new Set(blocks.map((b) => b.section));
    for (const s of sections || []) {
      if (populated.has(s.key) || s.key === 'intro' || s.key === 'closing') continue;
      rows.push(sectionRow(s.key, 'section:' + s.key + ':empty'));
    }
  }

  return (
    <aside
      className={
        'filmstrip' +
        (activeId ? ' filmstrip--dragging' : '') +
        (resizing ? ' filmstrip--resizing' : '') +
        (selected.size > 0 ? ' filmstrip--selecting' : '')
      }
      aria-label="Slides"
      style={width && !isPhone ? { width: width + 'px' } : undefined}
    >
      {!isPhone && (
        <div
          className="filmstrip__resize"
          onPointerDown={onResizeStart}
          onDoubleClick={onResizeReset}
          title="Drag to resize (double-click to reset)"
        />
      )}
      <div className="filmstrip__head">
        <span>Slides</span>
        <div className="filmstrip__head-actions">
          {onShowOverview && (slides || []).length > 0 && (
            <button
              type="button"
              className="iconbtn"
              onClick={onShowOverview}
              title="Overview: see all slides"
              aria-label="Overview: see all slides"
            >
              <LayoutGrid />
            </button>
          )}
          <button
            type="button"
            className="iconbtn"
            onClick={toggleCollapsed}
            title="Collapse the filmstrip to a rail"
            aria-label="Collapse slides"
          >
            <ChevronLeft />
          </button>
        </div>
      </div>
      {selected.size > 0 && (
        <div className="filmstrip__bulkbar" role="status">
          <span className="filmstrip__bulkbar-count">
            {selectedCount} slide{selectedCount === 1 ? '' : 's'} selected
          </span>
          <button type="button" className="linkbtn" onClick={clearSelection}>
            Clear
          </button>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => setActiveId(String(active.id))}
        onDragOver={({ over }) => setOverId(over ? String(over.id) : null)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setOverId(null);
        }}
      >
        {/* Ordered ids matching the rendered rows: a collapsed section stands in
            for its blocks, so collision detection sees one object where the
            list shows one row. */}
        <SortableContext items={sortableIds}>
          <ol className="filmstrip__list" role="list" ref={listRef}>
            {rows}
            {generating && (
              <li className="filmstrip__generating" aria-live="polite">
                <span className="filmstrip__spinner" aria-hidden="true" />
                Writing slides…
              </li>
            )}
          </ol>
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            isSectionId(activeId) ? (
              <SectionDragGhost
                section={sectionByKey.get(sectionIdKey(activeId))}
                count={(slides || []).filter((s) => s.section === sectionIdKey(activeId)).length}
              />
            ) : (
              <DragGhost block={blocks[dragFromIdx]} count={dragIdSet.size} sectionByKey={sectionByKey} />
            )
          ) : null}
        </DragOverlay>
      </DndContext>
      {addingSection ? (
        <AddSectionForm
          sections={sections}
          onCreate={(args) => {
            setAddingSection(false);
            onCreateSection?.(args);
          }}
          onCancel={() => setAddingSection(false)}
          busy={busy}
        />
      ) : (
        <div className="filmstrip__foot">
          {onAddSlide && (
            <button
              type="button"
              className="filmstrip__add filmstrip__add--slide"
              onClick={() => onAddSlide(null)}
              title="Add a slide: describe it in chat or pick a ready-made one"
              aria-label="Add a slide"
            >
              <Plus /> Slide
            </button>
          )}
          {(onAddSection || onCreateSection) && (
            <button
              type="button"
              className="filmstrip__add filmstrip__add--section"
              onClick={() => (onAddSection ? onAddSection() : setAddingSection(true))}
              title="Add a new section"
            >
              <Plus /> Section
            </button>
          )}
        </div>
      )}

      {renaming && (
        <RenameSlideModal
          slide={renaming}
          busy={busy}
          onCancel={() => setRenaming(null)}
          onConfirm={(title) => {
            const s = renaming;
            setRenaming(null);
            if (title && title !== s.title) onRenameSlide?.(s, title);
          }}
        />
      )}
      {confirm?.kind === 'slide' && (
        <ConfirmModal
          title={confirm.multiCount > 1 ? 'Delete all instances?' : 'Delete slide?'}
          body={
            confirm.multiCount > 1
              ? `"${confirm.slide.title}" appears ${confirm.multiCount} times from one file: deleting removes every instance. This can't be undone.`
              : `"${confirm.slide.title}" will be permanently removed from the deck. This can't be undone.`
          }
          danger
          confirmLabel="Delete"
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const s = confirm.slide;
            setConfirm(null);
            onDeleteSlide?.(s);
          }}
        />
      )}
      {confirm?.kind === 'section' &&
        (() => {
          // Offer to keep the slides by merging them into the neighbouring
          // section (previous in view order, else next) — only when the
          // section has slides and a neighbour exists to receive them.
          const i = contentKeys.indexOf(confirm.section.key);
          const neighbourKey = confirm.count > 0 && i >= 0 ? contentKeys[i - 1] || contentKeys[i + 1] || null : null;
          const neighbour = neighbourKey ? sectionByKey.get(neighbourKey) : null;
          const name = confirm.section.name || confirm.section.key;
          const slidesWord = `${confirm.count} slide${confirm.count === 1 ? '' : 's'}`;
          return (
            <ConfirmModal
              title={`Delete section "${name}"?`}
              body={
                neighbour
                  ? `"${name}" has ${slidesWord}. You can keep them (they move into "${neighbour.name || neighbour.key}") or delete everything. This can't be undone.`
                  : `This permanently deletes the section and its ${slidesWord}, then renumbers the rest. This can't be undone.`
              }
              danger
              confirmLabel={neighbour ? `Delete section and ${slidesWord}` : 'Delete section'}
              secondaryLabel={neighbour ? 'Keep the slides' : null}
              onSecondary={
                neighbour
                  ? () => {
                      const sec = confirm.section;
                      const n = confirm.count;
                      setConfirm(null);
                      onDeleteSection?.(sec, n, neighbourKey);
                    }
                  : null
              }
              busy={busy}
              onCancel={() => setConfirm(null)}
              onConfirm={() => {
                const sec = confirm.section;
                const n = confirm.count;
                setConfirm(null);
                onDeleteSection?.(sec, n);
              }}
            />
          );
        })()}
    </aside>
  );
}

/**
 * One manifest row — 1..N tiles sharing a file — as a single sortable <li>.
 *
 * The whole tile is a drag handle (dnd-kit's 5px threshold keeps a plain click
 * a select), while the grip carries `attributes` + `setActivatorNodeRef` so it
 * is the KEYBOARD activator: focus it, Space to lift, arrows to move, Space to
 * drop, Escape to cancel. `onKeyDown` is stripped from the tile's listeners so
 * Space/Enter there still means "open this slide" rather than starting a drag.
 */
type SortableSlideBlockProps = Pick<
  FilmstripProps,
  | 'sections'
  | 'activeIdx'
  | 'activity'
  | 'pending'
  | 'review'
  | 'blocked'
  | 'busy'
  | 'generating'
  | 'onSelect'
  | 'onAddSlide'
  | 'onDuplicate'
  | 'onMoveToSection'
  | 'onChangeTemplate'
  | 'onToggleHidden'
  | 'onSaveToBank'
> & {
  block: SlideBlock;
  isSelected: boolean;
  isDragMember: boolean;
  /** Null when the parent has no multi-commit handler — no handler, no
   *  checkbox, since a selection would have nowhere to go. */
  onToggleSelect: ((baseId: string) => void) | null;
  onRangeSelect: (baseId: string) => void;
  hasSelection: boolean;
  sectionByKey: Map<string, DeckSection>;
  // RefObject<T>, not RefObject<T | null>: under @types/react 18 `useRef<T>(null)`
  // already yields `current: T | null`, and only the 19-style spelling is
  // rejected by the DOM `ref=` attribute this is forwarded to.
  activeRef: RefObject<HTMLButtonElement>;
  editable: boolean;
  dropBefore: boolean;
  dropAfter: boolean;
  menuFor: string | null;
  setMenuFor: (id: string | null) => void;
  /** Opens the rename dialog by handing UP the slide; null hides the item. */
  onRequestRename: ((slide: SlideEntry) => void) | null;
  onRequestDelete: (confirm: FilmstripConfirm) => void;
};

function SortableSlideBlock({
  block,
  isSelected,
  isDragMember,
  onToggleSelect,
  onRangeSelect,
  hasSelection,
  sectionByKey,
  sections,
  activeIdx,
  activeRef,
  activity,
  pending,
  review,
  blocked,
  editable,
  busy,
  generating,
  dropBefore,
  dropAfter,
  menuFor,
  setMenuFor,
  onSelect,
  onAddSlide,
  onDuplicate,
  onRequestRename,
  onMoveToSection,
  onChangeTemplate,
  onToggleHidden,
  onSaveToBank,
  onRequestDelete,
}: SortableSlideBlockProps) {
  const disabled = !editable || busy || generating;
  // The open menu is portalled to the body, so it places itself against this
  // kebab's rect. Only one tile in a block can have a menu open at a time
  // (menuFor holds a single id), so one ref is enough — it is attached to
  // whichever kebab that is.
  const kebabRef = useRef<HTMLButtonElement>(null);
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({
    id: block.baseId,
    disabled,
  });
  // Space/Enter on a tile selects the slide; only the grip starts a keyboard drag.
  const { onKeyDown: _dragKeyDown, ...dragListeners } = listeners || {};

  return (
    <li
      ref={setNodeRef}
      className={
        'filmstrip__li' +
        (isDragging || isDragMember ? ' filmstrip__li--dragging' : '') +
        (dropBefore ? ' filmstrip__li--drop-before' : '') +
        (dropAfter ? ' filmstrip__li--drop-after' : '')
      }
    >
      {block.items.map((s) => {
        const section = sectionByKey.get(s.section);
        const active = s.idx === activeIdx;
        // Insert zones sit on a block's LAST tile (and add-before on its FIRST)
        // — multi:N instances share one manifest row, so "insert between
        // instances" has nowhere to land.
        const lastOfBlock = s === block.items[block.items.length - 1];
        const firstOfBlock = s === block.items[0];
        return (
          <React.Fragment key={s.id}>
            <div
              className={
                'filmstrip__tile' +
                (active ? ' filmstrip__tile--active' : '') +
                (isSelected ? ' filmstrip__tile--selected' : '') +
                (s.hidden ? ' filmstrip__tile--hidden' : '') +
                (pending?.has(s.id) ? ' filmstrip__tile--pending' : '') +
                (menuFor === s.id ? ' filmstrip__tile--menu' : '')
              }
              style={cssVars({ '--section-color': colourHex(section?.color || 'ORANGE') })}
            >
              {editable && (
                <button
                  type="button"
                  className="filmstrip__grip"
                  ref={setActivatorNodeRef}
                  disabled={disabled}
                  {...attributes}
                  {...listeners}
                  title="Drag to reorder, or press Space, then use the arrow keys"
                  aria-label={'Reorder ' + s.title}
                >
                  <Drag size={11} />
                </button>
              )}
              {editable && onToggleSelect && firstOfBlock && (
                <button
                  type="button"
                  className="filmstrip__checkbox"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.shiftKey) onRangeSelect(block.baseId);
                    else onToggleSelect(block.baseId);
                  }}
                  role="checkbox"
                  aria-checked={isSelected}
                  title={isSelected ? 'Deselect slide' : 'Select slide: drag any selected slide to move them together'}
                  aria-label={(isSelected ? 'Deselect ' : 'Select ') + s.title}
                />
              )}
              <button
                type="button"
                ref={active ? activeRef : undefined}
                className="filmstrip__select"
                disabled={!onSelect}
                onClick={(e) => {
                  if (!onSelect) return;
                  if (editable && onToggleSelect && (e.metaKey || e.ctrlKey)) return onToggleSelect(block.baseId);
                  if (editable && onToggleSelect && e.shiftKey && hasSelection) return onRangeSelect(block.baseId);
                  onSelect(s.idx);
                }}
                onContextMenu={
                  editable
                    ? (e) => {
                        e.preventDefault();
                        setMenuFor(s.id);
                      }
                    : undefined
                }
                aria-current={active ? 'step' : undefined}
                title={s.title}
                {...dragListeners}
              >
                <span className="filmstrip__num">{String(s.idx + 1).padStart(2, '0')}</span>
                <span className="filmstrip__title">{s.title}</span>
                {/* A parked slide stays fully interactive — the row is dimmed,
                    never disabled — so the tag says what is true of the deck
                    ("not published"), not of the row. */}
                {s.hidden && (
                  <span className="filmstrip__hidden-tag" title="Hidden: kept in the deck, left out when published">
                    Hidden
                  </span>
                )}
                {/* An AI-proposed slide, in the deck so you can see it, but not
                    yours until you keep it — the tag is the whole distinction. */}
                {pending?.has(s.id) && (
                  <span
                    className="filmstrip__new-tag"
                    title="Proposed by the AI, awaiting your decision. Open it to keep or discard."
                  >
                    New
                  </span>
                )}
                {/* An AI edit to an EXISTING slide, still undecided. It may not
                    be on the canvas yet — a proposal that landed while the
                    slide was being edited is held back rather than overwriting
                    the work in progress (lib/userBusy.js) — so this tag is how
                    the deck says where it is waiting. */}
                {!pending?.has(s.id) && review?.has(s.id) && (
                  <span
                    className="filmstrip__review-tag"
                    title="An AI edit is waiting for your decision. Open the slide to keep or discard it."
                  >
                    Edit
                  </span>
                )}
                {/* Top of the intensity ladder: unlike New and Edit, which are
                    the accent because the slide is merely awaiting a decision,
                    this one fills in the error colour because the deck cannot be
                    published while it stands. */}
                {blocked?.has(s.id) && (
                  <span
                    className="filmstrip__blocked-tag"
                    title="This slide is showing bundled demo content (other companies’ logos, or a colleague’s photo). Publishing is blocked until it is replaced."
                  >
                    Check
                  </span>
                )}
              </button>
              {activity?.[s.id] && (
                <span
                  className={'filmstrip__activity filmstrip__activity--' + activity[s.id]}
                  title={
                    activity[s.id] === 'streaming'
                      ? 'Reply in progress'
                      : activity[s.id] === 'done'
                        ? 'Slide written: the run is finishing'
                        : 'Reply waiting'
                  }
                />
              )}
              {/* `disabled`, not `editable`, so it closes DURING A BUILD too.
                  It was gated on `editable` alone, which meant `generating`
                  blocked the one harmless interaction (drag to reorder) and left
                  every destructive one live: delete, change template and move to
                  section all ran against files the agent was writing
                  (Alex, 2026-08-18). Looking stays available; mutating does not. */}
              {editable && (
                <button
                  type="button"
                  ref={menuFor === s.id ? kebabRef : undefined}
                  className="filmstrip__kebab iconbtn"
                  disabled={disabled}
                  onClick={() => setMenuFor(menuFor === s.id ? null : s.id)}
                  title={disabled ? 'Slide options: unavailable while the deck is being written' : 'Slide options'}
                  aria-label={'Options for ' + s.title}
                  aria-haspopup="menu"
                >
                  <Kebab size={12} />
                </button>
              )}
              {editable && menuFor === s.id && (
                <SlideContextMenu
                  slide={s}
                  anchor={kebabRef}
                  sections={(sections || []).filter((x) => sectionByKey.has(x.key))}
                  multiCount={block.items.length}
                  onAddAfter={editable && onAddSlide ? () => onAddSlide(s.section, s) : null}
                  onDuplicate={() => onDuplicate?.(s)}
                  onRename={onRequestRename ? () => onRequestRename(s) : null}
                  onMoveToSection={(key) => onMoveToSection?.(s, key)}
                  onChangeTemplate={onChangeTemplate ? () => onChangeTemplate(s) : null}
                  onToggleHidden={onToggleHidden ? () => onToggleHidden(s) : null}
                  onSaveToBank={onSaveToBank ? (bank) => onSaveToBank(s, bank) : null}
                  onDelete={() => onRequestDelete({ kind: 'slide', slide: s, multiCount: block.items.length })}
                  onClose={() => setMenuFor(null)}
                  busy={busy}
                />
              )}
            </div>
            {editable && onAddSlide && lastOfBlock && (
              <button
                type="button"
                className="filmstrip__insert"
                disabled={disabled}
                onClick={() => onAddSlide(s.section, s)}
                title={
                  disabled ? 'Cannot add a slide while the deck is being written' : `Insert a slide after "${s.title}"`
                }
                aria-label={'Insert a slide after ' + s.title}
              >
                <span className="filmstrip__insert-dot" aria-hidden="true">
                  <Plus size={9} />
                </span>
              </button>
            )}
          </React.Fragment>
        );
      })}
    </li>
  );
}

/** A collapsed section row as a sortable item. Renders its own <li> and hands
 *  the drag grip to the header via a render prop, so the WHOLE row is the drop
 *  target while only the grip starts the drag (same split as a slide tile). */
function SortableSectionRow({
  sectionKey,
  children,
}: {
  sectionKey: string;
  /** A render prop, not a node: the row owns the drop target while only the
   *  grip it hands down starts the drag. */
  children: (grip: ReactNode) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({
    id: sectionDragId(sectionKey),
  });
  const grip = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="strip-section__grip"
      title="Drag to move this section"
      aria-label={'Move the ' + sectionKey + ' section'}
      {...attributes}
      {...listeners}
    >
      <Drag size={13} />
    </button>
  );
  return (
    <li ref={setNodeRef} className={'filmstrip__section-row' + (isDragging ? ' filmstrip__section-row--dragging' : '')}>
      {children(grip)}
    </li>
  );
}

/** What rides the cursor while a section is in flight: its name and how many
 *  slides are coming with it. */
function SectionDragGhost({ section, count }: { section: DeckSection | undefined; count: number }) {
  if (!section) return null;
  return (
    <div className="strip-section strip-section--ghost">
      <span
        className="strip-section__dot strip-section__dot--static"
        style={cssVars({ '--swatch': colourHex(section.color || 'ORANGE') })}
      />
      <span className="strip-section__name">
        {section.n ? section.n + ' · ' : ''}
        {section.name || section.key}
      </span>
      <span className="strip-section__count">{count}</span>
    </div>
  );
}

/** Small prompt dialog for renaming a slide's meta title. Enter confirms,
 *  Escape cancels; the input opens pre-filled and selected. */
function RenameSlideModal({
  slide,
  busy,
  onCancel,
  onConfirm,
}: {
  slide: SlideEntry;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (title: string) => void;
}) {
  const [draft, setDraft] = useState(slide.title || '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function submit() {
    const title = draft.trim();
    if (title) onConfirm(title);
    else onCancel();
  }

  return (
    <Modal title="Rename slide" onClose={onCancel} className="modal--confirm">
      <div className="modal__body">
        <input
          ref={inputRef}
          type="text"
          className="modal__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          maxLength={120}
          aria-label="Slide title"
        />
        <p className="muted small" style={{ marginTop: '0.5rem' }}>
          The title names the slide in the filmstrip and overview; it does not change what the slide shows.
        </p>
      </div>
      <footer className="modal__footer">
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={busy || !draft.trim()}>
          Rename
        </button>
      </footer>
    </Modal>
  );
}

/** The tile that follows the cursor mid-drag (dnd-kit's DragOverlay). A
 *  multi-drag stacks offset card shadows behind it and wears a count badge. */
function DragGhost({
  block,
  count = 1,
  sectionByKey,
}: {
  /** Undefined while nothing is in flight — the overlay renders on every drag
   *  state, including the one where `blocks[dragFromIdx]` is a miss. */
  block: SlideBlock | undefined;
  count?: number;
  sectionByKey: Map<string, DeckSection>;
}) {
  const first = block?.items[0];
  if (!first) return null;
  const section = sectionByKey.get(first.section);
  return (
    <div
      className={'filmstrip__tile filmstrip__tile--ghost' + (count > 1 ? ' filmstrip__tile--ghost-stack' : '')}
      style={cssVars({ '--section-color': colourHex(section?.color || 'ORANGE') })}
    >
      {count > 1 && (
        <span className="filmstrip__ghost-badge" aria-hidden="true">
          {count}
        </span>
      )}
      <span className="filmstrip__grip">
        <Drag size={11} />
      </span>
      <span className="filmstrip__select">
        <span className="filmstrip__num">{String(first.idx + 1).padStart(2, '0')}</span>
        <span className="filmstrip__title">{first.title}</span>
      </span>
    </div>
  );
}
